import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { CheckCircle2, XCircle, Lightbulb } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { DatumVeld, geldigeDatum, teVroeg, vandaagNL, TE_VROEG } from "@/components/DatumVeld";
import { kortDatum } from "@/components/afspraken/afsprakenUtils";

const STATUS_LABEL = { nagekomen: "Nagekomen", niet: "Niet nagekomen" };

export default function AfspraakAfsluitDialog({ open, onOpenChange, afspraak, status, onSaved, vervolgStandaard = false }) {
  const [samenvatting, setSamenvatting] = useState("");
  const [analyse, setAnalyse] = useState("");
  const [saving, setSaving] = useState(false);
  const [vervolg, setVervolg] = useState(false);
  const [vDeadline, setVDeadline] = useState("");
  const [vFout, setVFout] = useState("");

  useEffect(() => {
    if (open && afspraak) {
      setSamenvatting(afspraak.samenvatting || "");
      setAnalyse(afspraak.analyse || "");
      setVervolg(!!vervolgStandaard); setVDeadline(""); setVFout("");
    }
  }, [open, afspraak, vervolgStandaard]);

  const save = async () => {
    if (saving) return;
    const metVervolg = status === "niet" && vervolg && !afspraak?.vervolgInfo;
    if (metVervolg && !geldigeDatum(vDeadline)) {
      const m = vDeadline ? "Kies een nieuwe deadline tussen het jaar 2000 en 2100." : "Kies een datum voor de nieuwe deadline, of zet het plannen uit.";
      setVFout(m); toast.error(m); return;
    }
    if (metVervolg && teVroeg(vDeadline, "")) { setVFout(TE_VROEG); toast.error(TE_VROEG); return; }
    setSaving(true);
    try {
      const res = await api.patch(`/afspraken/${afspraak.id}/status`, {
        status,
        samenvatting: samenvatting,
        analyse: analyse,
        ...(metVervolg ? { vervolgDeadline: vDeadline } : {}),
      });
      toast.success(metVervolg ? "Afspraak afgerond. Vervolgafspraak aangemaakt." : "Status bijgewerkt");
      onSaved && onSaved(res.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bijwerken mislukt");
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    "w-full px-3 py-2.5 rounded-md border border-zinc-300 focus:outline-none focus:ring-2 focus:ring-blue-600 text-sm text-zinc-900";
  const heeftVervolg = status === "niet" && !!afspraak?.vervolgInfo;
  const Icon = status === "nagekomen" ? CheckCircle2 : XCircle;
  const accent = status === "nagekomen" ? "text-emerald-600" : "text-rose-600";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-w-[calc(100vw-1.5rem)] max-h-[90vh] overflow-y-auto" data-testid="afspraak-afsluit-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Icon className={`h-5 w-5 ${accent}`} /> Afspraak afronden — {STATUS_LABEL[status] || status}
          </DialogTitle>
          <DialogDescription>
            Geen autopilot: leg kort vast wat er is gebeurd en reflecteer op je handelen. Beide velden zijn optioneel.
          </DialogDescription>
        </DialogHeader>
        {afspraak && (
          <div className="rounded-md bg-zinc-50 border border-zinc-200 px-3 py-2 text-sm text-zinc-700">
            <span className="font-semibold whitespace-pre-line break-words">{afspraak.wat}</span>
            {afspraak.locatie && <span className="text-zinc-400"> · {afspraak.locatie}</span>}
          </div>
        )}
        <div className="space-y-4 py-1">
          <div>
            <label className="block text-sm font-semibold text-zinc-800 mb-1">
              <span className="text-zinc-400 font-mono text-xs mr-1">1.</span> Samenvatting
            </label>
            <p className="text-xs text-zinc-500 mb-1.5">Wat is er gebeurd / hoe is het afgehandeld?</p>
            <textarea data-testid="afsluit-samenvatting" rows={3} className={inputCls}
              value={samenvatting} onChange={(e) => setSamenvatting(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-semibold text-zinc-800 mb-1 flex items-center gap-1.5">
              <span className="text-zinc-400 font-mono text-xs mr-1">2.</span>
              <Lightbulb className="h-4 w-4 text-amber-500" /> Kritische analyse — reflectie op het handelen
            </label>
            <p className="text-xs text-zinc-500 mb-1.5">Wat ging goed, wat kon beter, wat neem je mee?</p>
            <textarea data-testid="afsluit-analyse" rows={3} className={inputCls}
              value={analyse} onChange={(e) => setAnalyse(e.target.value)} />
          </div>
          {heeftVervolg && (
            <p data-testid="afsluit-heeft-vervolg" className="rounded-md border border-zinc-200 px-3 py-2 text-sm text-zinc-700">
              Deze afspraak heeft al een vervolg (deadline {kortDatum(afspraak.vervolgInfo.deadline) || "onbekend"}).
            </p>
          )}
          {status === "niet" && !heeftVervolg && (
            <div className="rounded-md border border-zinc-200 p-3 space-y-2" data-testid="afsluit-vervolg-blok">
              <label className="flex items-center gap-3 min-h-[44px] sm:min-h-0 cursor-pointer text-sm font-semibold text-zinc-800">
                <input type="checkbox" data-testid="afsluit-vervolg-check" checked={vervolg} onChange={(e) => setVervolg(e.target.checked)} className="h-5 w-5 sm:h-4 sm:w-4 accent-blue-600" />
                Nieuwe deadline plannen
              </label>
              {vervolg && (
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-1">Nieuwe deadline</label>
                  <DatumVeld testid="afsluit-vervolg-deadline" value={vDeadline} onChange={(v) => { setVDeadline(v); setVFout(""); }} min={vandaagNL()} aria-label="Nieuwe deadline" />
                  {vFout && <p data-testid="afsluit-vervolg-fout" role="alert" className="text-xs font-semibold text-rose-600 mt-1.5">{vFout}</p>}
                </div>
              )}
              <p className="text-xs text-zinc-500">De afspraak blijft staan als niet nagekomen. Er komt een nieuwe afspraak met dezelfde tekst, locatie en opvolger, met deze deadline.</p>
            </div>
          )}
        </div>
        <DialogFooter>
          <button onClick={() => onOpenChange(false)} className="w-full sm:w-auto px-4 py-2.5 rounded-md text-sm font-medium text-zinc-500 hover:bg-zinc-100">Annuleren</button>
          <button data-testid="afsluit-save" onClick={save} disabled={saving}
            className="w-full sm:w-auto px-4 py-2.5 rounded-md text-sm font-semibold bg-zinc-950 text-white hover:bg-zinc-800 disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2">
            {saving ? "Bezig…" : (status === "niet" && vervolg && !heeftVervolg ? "Afronden en vervolg plannen" : "Afronden")}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
